import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Loader2, Pencil, FolderKanban, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { StepProject } from './AddToCrm/StepProject';
import { StepPackage } from './AddToCrm/StepPackage';
import { INPUT_CLS } from './AddToCrm/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
  EditCrmQuoteModal — the "Edit" button on an already-in-CRM quote card.
  Two paths, chosen up front:

    'amendment' — edits the EXISTING Zoho Quote record in place (subject,
                  supplier). Calls PATCH /zoho/update-quote/{quote_id}.
                  NEVER creates a new record.

    'project'   — links/moves the Quote's Project (Deal) and Package
                  (Contractor_Package / Sub_Contractor_Package). Reuses
                  StepProject/StepPackage from AddToCrmModal — same search,
                  same "create new" flows — just writing the result onto
                  the EXISTING quote via PATCH /zoho/update-quote-project/{quote_id}
                  instead of creating a new Quote.

  Props:
    quote        — { zoho_quote_id, zoho_quote_url, customer_name, subject,
                      supplier_name, deal_id, deal_name,
                      contractor_package_id, sub_contractor_package_id }
                    (whatever subset of these fields Emergent already has
                    available on the recent-quotes card — extend the
                    /quotes/recent/all response to include zoho_quote_id
                    and the current project/package if not already there)
*/
export default function EditCrmQuoteModal({ isOpen, onClose, quote, onSaved }) {
  const [path, setPath] = useState(null); // null | 'amendment' | 'project'

  // ── Amendment state ──────────────────────────────────────────────────
  const [subject, setSubject] = useState(quote?.subject || '');
  const [supplierQuery, setSupplierQuery] = useState(quote?.supplier_name || '');
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [savingAmendment, setSavingAmendment] = useState(false);

  useEffect(() => {
    if (path !== 'amendment') return;
    const t = setTimeout(async () => {
      if (!supplierQuery?.trim()) { setSuppliers([]); return; }
      setSuppliersLoading(true);
      try {
        const res = await api.get(`${API}/zoho/search-suppliers`, { params: { q: supplierQuery } });
        setSuppliers(res.data?.suppliers || []);
      } catch { setSuppliers([]); } finally { setSuppliersLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [supplierQuery, path]);

  const saveAmendment = async () => {
    if (!subject.trim()) return toast.error('Subject is required');
    setSavingAmendment(true);
    try {
      // PATCH — updates the existing record. Never creates a new Quote.
      await api.patch(`${API}/zoho/update-quote/${quote.zoho_quote_id}`, {
        subject: subject.trim(),
        supplier_id: supplierId || null, // null = leave supplier unchanged
      });
      toast.success('Quote updated in Zoho CRM');
      window.dispatchEvent(new Event('c30:quotes-changed'));
      onSaved?.({ subject: subject.trim(), ...(supplierId ? { supplier_id: supplierId, supplier_name: supplierQuery } : {}) });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update quote');
    } finally {
      setSavingAmendment(false);
    }
  };

  // ── Project/package state — reuses the same StepProject/StepPackage
  // components AddToCrmModal already uses, just wired to a PATCH at the end
  // instead of the create-quote POST. ─────────────────────────────────────
  const [projectQuery, setProjectQuery] = useState(quote?.deal_name || '');
  const [projectLoading, setProjectLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(
    quote?.deal_id ? { id: quote.deal_id, deal_name: quote.deal_name } : null
  );
  const [showProjectCreate, setShowProjectCreate] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectBarbourId, setNewProjectBarbourId] = useState('');
  const [newProjectPostcode, setNewProjectPostcode] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [step, setStep] = useState(1); // 1 = project, 2 = package

  const [contractorRole, setContractorRole] = useState('main');
  const [projectPackages, setProjectPackages] = useState({ contractor_packages: [], sub_contractor_packages: [] });
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [selectedContractorPackageId, setSelectedContractorPackageId] = useState(quote?.contractor_package_id || '');
  const [selectedSubContractorPackageId, setSelectedSubContractorPackageId] = useState(quote?.sub_contractor_package_id || '');
  const [selectedContractorAccountId, setSelectedContractorAccountId] = useState('');
  const [packageTypes, setPackageTypes] = useState({ contractor_package_types: [], sub_contractor_package_types: [] });
  const [packageTypesLoading, setPackageTypesLoading] = useState(false);
  const [showNewPackage, setShowNewPackage] = useState(false);
  const [newContractorAccountQuery, setNewContractorAccountQuery] = useState('');
  const [newContractorAccounts, setNewContractorAccounts] = useState([]);
  const [newContractorAccountLoading, setNewContractorAccountLoading] = useState(false);
  const [newContractorAccount, setNewContractorAccount] = useState(null);
  const [newPackageType, setNewPackageType] = useState('');
  const [newPackageParentCP, setNewPackageParentCP] = useState('');
  const [newPackageCategories, setNewPackageCategories] = useState([]);
  const [newPackageEnconStartDate, setNewPackageEnconStartDate] = useState('');
  const [creatingPackage, setCreatingPackage] = useState(false);
  const [savingProject, setSavingProject] = useState(false);

  useEffect(() => {
    if (path !== 'project' || !selectedProject?.id) return;
    setPackagesLoading(true);
    api.get(`${API}/zoho/project/${selectedProject.id}/packages`)
      .then(res => setProjectPackages(res.data || { contractor_packages: [], sub_contractor_packages: [] }))
      .catch(() => setProjectPackages({ contractor_packages: [], sub_contractor_packages: [] }))
      .finally(() => setPackagesLoading(false));
  }, [path, selectedProject]);

  useEffect(() => {
    if (path !== 'project') return;
    setPackageTypesLoading(true);
    api.get(`${API}/zoho/package-types`)
      .then(res => setPackageTypes(res.data || { contractor_package_types: [], sub_contractor_package_types: [] }))
      .catch(() => {})
      .finally(() => setPackageTypesLoading(false));
  }, [path]);

  const runProjectSearch = async (q) => {
    if (!q || q.trim().length < 2) return setProjects([]);
    setProjectLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-projects`, { params: { q } });
      setProjects(res.data.projects || []);
    } catch { setProjects([]); } finally { setProjectLoading(false); }
  };
  useEffect(() => {
    if (path !== 'project') return;
    const t = setTimeout(() => runProjectSearch(projectQuery), 350);
    return () => clearTimeout(t);
  }, [projectQuery, path]);

  const runContractorAccountSearch = async (q) => {
    if (!q || q.trim().length < 2) return setNewContractorAccounts([]);
    setNewContractorAccountLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-accounts`, { params: { q } });
      setNewContractorAccounts(res.data.accounts || []);
    } catch { setNewContractorAccounts([]); } finally { setNewContractorAccountLoading(false); }
  };
  useEffect(() => {
    if (!showNewPackage) return;
    const t = setTimeout(() => runContractorAccountSearch(newContractorAccountQuery), 300);
    return () => clearTimeout(t);
  }, [newContractorAccountQuery, showNewPackage]);

  const createProject = async () => {
    if (!newProjectName.trim()) return toast.error('Project name is required');
    setCreatingProject(true);
    try {
      const res = await api.post(`${API}/zoho/create-project`, {
        deal_name: newProjectName.trim(),
        barbour_project_id: newProjectBarbourId.trim() || null,
        account_id: null,
        postcode: newProjectPostcode.trim() || null,
      });
      const p = { id: res.data.id, deal_name: res.data.deal_name, barbour_project_id: res.data.barbour_project_id, postcode: newProjectPostcode.trim() || null };
      setSelectedProject(p);
      setShowProjectCreate(false);
      toast.success('Project created in Zoho CRM');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create project');
    } finally { setCreatingProject(false); }
  };

  const createPackage = async () => {
    if (!newContractorAccount?.id) return toast.error('Pick a contractor account');
    if (!newPackageType) return toast.error('Pick a package type');
    if (!selectedProject?.id) return toast.error('Project is required');
    setCreatingPackage(true);
    try {
      const endpoint = contractorRole === 'main' ? 'create-contractor-package' : 'create-subcontractor-package';
      const payload = contractorRole === 'main'
        ? { project_id: selectedProject.id, contractor_account_id: newContractorAccount.id, package_type: newPackageType, encon_start_date: newPackageEnconStartDate || null }
        : { project_id: selectedProject.id, sub_contractor_account_id: newContractorAccount.id, sub_contractor_package_type: newPackageType, parent_contractor_package_id: newPackageParentCP || null, package_categories: newPackageCategories.length > 0 ? newPackageCategories : null, encon_start_date: newPackageEnconStartDate || null };
      const res = await api.post(`${API}/zoho/${endpoint}`, payload);
      if (contractorRole === 'main') setSelectedContractorPackageId(res.data.id);
      else setSelectedSubContractorPackageId(res.data.id);
      setShowNewPackage(false);
      toast.success(`${contractorRole === 'main' ? 'Main' : 'Sub'} contractor package created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create package');
    } finally { setCreatingPackage(false); }
  };

  const saveProjectLink = async () => {
    if (!selectedProject?.id) return toast.error('Pick or create a project first');
    setSavingProject(true);
    try {
      // PATCH — updates the EXISTING quote's Deal_Name / Contractor_Package /
      // Sub_Contractor_Package lookup fields. Never creates a new Quote.
      await api.patch(`${API}/zoho/update-quote-project/${quote.zoho_quote_id}`, {
        deal_id: selectedProject.id,
        contractor_package_id: selectedContractorPackageId || null,
        sub_contractor_package_id: selectedSubContractorPackageId || null,
      });
      toast.success('Quote moved to project in Zoho CRM');
      window.dispatchEvent(new Event('c30:quotes-changed'));
      onSaved?.({
        deal_id: selectedProject.id,
        deal_name: selectedProject.deal_name,
        contractor_package_id: selectedContractorPackageId || null,
        sub_contractor_package_id: selectedSubContractorPackageId || null,
      });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update project link');
    } finally { setSavingProject(false); }
  };

  const step3Ready = contractorRole === 'main' ? !!selectedContractorPackageId : !!selectedSubContractorPackageId;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Edit CRM Quote</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {!path && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setPath('amendment')}
                className="rounded-2xl border-2 border-gray-200 hover:border-blue-400 p-5 text-left transition-colors"
                data-testid="edit-crm-path-amendment"
              >
                <Pencil className="w-6 h-6 text-blue-600 mb-2" />
                <div className="font-bold text-gray-900">Edit this quote</div>
                <div className="text-xs text-gray-500 mt-1">Change the subject or supplier — updates this exact record, doesn't create a new one.</div>
              </button>
              <button
                onClick={() => setPath('project')}
                className="rounded-2xl border-2 border-gray-200 hover:border-blue-400 p-5 text-left transition-colors"
                data-testid="edit-crm-path-project"
              >
                <FolderKanban className="w-6 h-6 text-blue-600 mb-2" />
                <div className="font-bold text-gray-900">
                  {quote?.deal_id ? 'Move to a different project' : 'Add to a project'}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {quote?.deal_id
                    ? `Currently: ${quote.deal_name || '—'}`
                    : 'Link this quote to a project and package.'}
                </div>
              </button>
            </div>
          )}

          {path === 'amendment' && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} className={`mt-1 ${INPUT_CLS}`} data-testid="edit-crm-subject-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Supplier</Label>
                <Input
                  value={supplierQuery}
                  onChange={(e) => { setSupplierQuery(e.target.value); setSupplierId(''); }}
                  placeholder="Search supplier — leave as-is to keep unchanged"
                  className={`mt-1 ${INPUT_CLS}`}
                  data-testid="edit-crm-supplier-input"
                />
                {!supplierId && suppliers.length > 0 && (
                  <div className="mt-2 rounded-lg border border-gray-200 max-h-40 overflow-y-auto">
                    {suppliersLoading ? (
                      <div className="p-2 text-xs text-gray-500">Searching…</div>
                    ) : suppliers.slice(0, 20).map(s => (
                      <button key={s.id} onClick={() => { setSupplierId(s.id); setSupplierQuery(s.name); }}
                        className="w-full text-left text-sm px-3 py-2 hover:bg-blue-50">
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
                {supplierId && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" /> {supplierQuery}
                  </div>
                )}
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={() => setPath(null)}>← Back</Button>
                <Button onClick={saveAmendment} disabled={savingAmendment} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {savingAmendment ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving…</> : 'Save amendment'}
                </Button>
              </div>
            </div>
          )}

          {path === 'project' && (
            <div className="space-y-4">
              <StepProject
                step={step} mode="project" showAccessRequest={false}
                projectQuery={projectQuery} setProjectQuery={setProjectQuery}
                projectLoading={projectLoading} projects={projects}
                selectedProject={selectedProject} setSelectedProject={setSelectedProject}
                showProjectCreate={showProjectCreate} setShowProjectCreate={setShowProjectCreate}
                newProjectName={newProjectName} setNewProjectName={setNewProjectName}
                newProjectBarbourId={newProjectBarbourId} setNewProjectBarbourId={setNewProjectBarbourId}
                newProjectPostcode={newProjectPostcode} setNewProjectPostcode={setNewProjectPostcode}
                creatingProject={creatingProject} runProjectSearch={runProjectSearch}
                createProject={createProject} quoteForm={quote} setStep={setStep}
                onProjectsUpdate={() => {}}
              />
              {selectedProject && (
                <StepPackage
                  step={2} mode="project" showAccessRequest={false}
                  selectedProject={selectedProject}
                  effectiveForm={quote}
                  contractorRole={contractorRole} setContractorRole={setContractorRole}
                  selectedContractorPackageId={selectedContractorPackageId} setSelectedContractorPackageId={setSelectedContractorPackageId}
                  selectedSubContractorPackageId={selectedSubContractorPackageId} setSelectedSubContractorPackageId={setSelectedSubContractorPackageId}
                  selectedContractorAccountId={selectedContractorAccountId} setSelectedContractorAccountId={setSelectedContractorAccountId}
                  showNewPackage={showNewPackage} setShowNewPackage={setShowNewPackage}
                  newContractorAccountQuery={newContractorAccountQuery} setNewContractorAccountQuery={setNewContractorAccountQuery}
                  newContractorAccount={newContractorAccount} setNewContractorAccount={setNewContractorAccount}
                  newContractorAccounts={newContractorAccounts} newContractorAccountLoading={newContractorAccountLoading}
                  newPackageType={newPackageType} setNewPackageType={setNewPackageType}
                  newPackageParentCP={newPackageParentCP} setNewPackageParentCP={setNewPackageParentCP}
                  newPackageCategories={newPackageCategories} setNewPackageCategories={setNewPackageCategories}
                  newPackageEnconStartDate={newPackageEnconStartDate} setNewPackageEnconStartDate={setNewPackageEnconStartDate}
                  packagesLoading={packagesLoading} projectPackages={projectPackages} packageTypes={packageTypes}
                  packageTypesLoading={packageTypesLoading} onRetryPackageTypes={() => {}}
                  mainContractorCount={0} subContractorCount={0}
                  creatingPackage={creatingPackage} createPackage={createPackage}
                  step3Ready={step3Ready} setStep={setStep}
                />
              )}
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={() => setPath(null)}>← Back</Button>
                <Button onClick={saveProjectLink} disabled={savingProject || !selectedProject?.id} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {savingProject ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving…</> : (quote?.deal_id ? 'Move quote to this project' : 'Link quote to this project')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
